'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import Card from '@/components/product/Card';
import { v4 as uuidv4 } from 'uuid';
import { getAllProduct } from '@/action/action';
import { useSearchParams } from 'next/navigation';
import { Baskervville, Open_Sans } from 'next/font/google';
import Loader from '@/components/loading-spinner';
const baskervville = Baskervville({ weight: ['400'], style: ['normal', 'italic'], subsets: ['latin'], display: 'swap' });
const open_sans = Open_Sans({ weight: ['400', '700', '800'], style: ['normal', 'italic'], subsets: ['latin'], display: 'swap' });

const PromotionProduct = ({ initialProducts }) => {
    const searchParamsHook = useSearchParams();
    const [products, setProducts] = useState(initialProducts?.productList || []);
    const [page, setPage] = useState(2);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const observer = useRef();

    const getParam = (key) => searchParamsHook.get(key);

    const buildProductUrl = useCallback((page) => {
        let url = `/v1/products/get-all-products?page=${page}&limit=12&stock_product=true&random=yes`;

        const price = getParam('price');
        const category = getParam('category');
        const parentCategory = getParam('parentCategory')
        const brand = getParam('brand');
        const limited = getParam('limited');

        if (limited) {
            url += `&discount_range=${Number(limited)}`;
        }
        if (category) {
            const categoryArray = category.split(',').map(tag => `"${tag.trim()}"`);
            url += `&filter[child_category_id][$in]=[${categoryArray.join(',')}]`;
        }
        if (parentCategory) {
            const categoryArray = parentCategory?.split(',').map(tag => `"${tag.trim()}"`);
            url += `&filter[parent_category_id][$in]=[${categoryArray.join(',')}]`;
        }
        if (brand) {
            const brandArray = brand.split(',').map(tag => `"${tag.trim()}"`);
            url += `&filter[brand_id][$in]=[${brandArray.join(',')}]`;
        }
        if (price) {
            url += `&min_price=100&max_price=${price}&sort[list_price]=-1`;
        }

        return url;
    }, [searchParamsHook]);

    const loadMoreProducts = async () => {
        setLoading(true);
        const url = buildProductUrl(page);
        const res = await getAllProduct(url);

        const newProducts = res?.productList || [];
        setProducts(prev => [...prev, ...newProducts]);
        setHasMore(newProducts.length > 0);
        setPage(prev => prev + 1);
        setLoading(false);
    };

    const lastProductRef = useCallback(
        (node) => {
            if (loading) return;
            if (observer.current) observer.current.disconnect();

            observer.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && hasMore) {
                    loadMoreProducts();
                }
            });

            if (node) observer.current.observe(node);
        },
        [loading, hasMore]
    );

    // Re-fetch on query param change
    useEffect(() => {
        const fetchNewProducts = async () => {
            setLoading(true);
            setPage(2);
            const url = buildProductUrl(1);
            const res = await getAllProduct(url);
            setProducts(res?.productList || []);
            setHasMore(true);
            setLoading(false);
        };

        fetchNewProducts();
    }, [searchParamsHook.toString()]); // ← triggers on any query change

    return (
        <>
            <div className='mt-2 grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-5 xl:grid-cols-4 p-4 md:scroll-mt-28'>
                {products.map((product, index) => {
                    const isLast = index === products.length - 1;
                    return (
                        <div ref={isLast ? lastProductRef : null} key={uuidv4()}>
                            <Card product={product} />
                        </div>
                    );
                })}
            </div>
            {loading && (
                <div className="w-full h-40 flex justify-center items-center text-center">
                    <Loader />
                </div>
            )}
            {!hasMore && (
                <div className='flex flex-col py-5 md:py-7 lg:py-10'>
                    <h3 className={`${baskervville.className} text-center text-4xl md:text-5xl lg:text-7xl font-normal leading-snug text-black`}>
                        <span className={`font-sans font-extrabold ${open_sans.className}`}>OK,</span><br />
                        show me <span className={`font-sans font-bold ${open_sans.className}`}>all</span><br />
                        products!
                    </h3>
                    <a href='/product' className='flex items-center justify-center text-lg uppercase underline mb-5'>
                        Discover More {` -->`}
                    </a>
                </div>
            )}
        </>
    );
};

export default PromotionProduct;
